import { Injectable, signal, Signal } from '@angular/core';
import { RxStomp, RxStompState } from '@stomp/rx-stomp';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import SockJS from 'sockjs-client';
import { environment } from 'environments/environment';

export type ConnectionState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {
    private rxStomp: RxStomp;
    private connectionStateSignal = signal<ConnectionState>('DISCONNECTED');

    // Read-only signal for components to observe connection state
    public readonly connectionState: Signal<ConnectionState> = this.connectionStateSignal.asReadonly();

    constructor() {
        this.rxStomp = new RxStomp();

        // Listen to connection state changes
        this.rxStomp.connectionState$.subscribe(state => {
            switch (state) {
                case RxStompState.CONNECTING:
                    this.connectionStateSignal.set('CONNECTING');
                    break;
                case RxStompState.OPEN:
                    this.connectionStateSignal.set('CONNECTED');
                    break;
                case RxStompState.CLOSING:
                case RxStompState.CLOSED:
                    this.connectionStateSignal.set('DISCONNECTED');
                    break;
                default:
                    // RxStomp doesn't have an explicit RECONNECTING state, it just goes back to CONNECTING
                    break;
            }
        });
    }

    /**
     * Connect to the WebSocket server using the provided JWT token
     */
    connect(token: string): void {
        if (!token) return;

        // Configure RxStomp
        this.rxStomp.configure({
            // Fallback for browsers that don't support WebSockets
            webSocketFactory: () => {
                // Determine base URL, sockjs client appends paths
                const url = environment.wsUrl;
                // Important: SockJS needs the token in the URL or headers
                // Spring Security in our config typically accepts it as a parameter for SockJS if needed,
                // or we can put it in headers. Since SockJS doesn't support custom headers easily,
                // we'll pass the token in connectHeaders for standard STOMP and as a URL param if needed.
                return new SockJS(`${url}?access_token=${token}`);
            },

            // Headers to pass during connection
            connectHeaders: {
                Authorization: `Bearer ${token}`
            },

            // How often to heartbeat (in ms)
            heartbeatIncoming: 0, // Typical: 0 (disable) or 10000
            heartbeatOutgoing: 20000,

            // Wait in milliseconds before attempting auto reconnect
            reconnectDelay: 5000,

            // Set to true to see debug logs
            debug: (msg: string): void => {
                if (!environment.production) {
                    console.log(new Date(), msg);
                }
            }
        });

        this.rxStomp.activate();
    }

    /**
     * Disconnect from the WebSocket server
     */
    disconnect(): void {
        this.rxStomp.deactivate();
        this.connectionStateSignal.set('DISCONNECTED');
    }

    /**
     * Subscribe to a specific STOMP destination
     * @param destination e.g. /user/queue/notifications
     */
    watch<T>(destination: string): Observable<T> {
        return this.rxStomp.watch(destination).pipe(
            map(message => JSON.parse(message.body) as T)
        );
    }
}
